import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ThemeProvider, useTheme } from '../home/context/ThemeContext';
import SiteFooter from '../../components/feature/SiteFooter';
import SiteNav from '../../components/feature/SiteNav';
import PageHero, { gradientTextStyle } from '../../components/feature/PageHero';
import { useSEO } from '../../utils/seo';

// ... existing code (stats, useInView, AnimatedNumber, StatCard, NumberSide, TextSide, StatisticsSection, Comparison, FAQ, CTA, Sources) stays the same ...

// ─── Data ─────────────────────────────────────────────────────────────────────
const stats = [
  {
    id: 1, displayNumber: '10,1', numericFrom: 0, numericTo: 10.1, suffix: '%', prefix: '', isX: false, countDown: false,
    thesis: 'mehr Conversion sind schon durch 0,1 Sekunden bessere Ladezeit möglich.',
    copy: 'Schon kleine Performance-Verbesserungen können messbar mehr Leads bringen.',
    source: 'Google / Deloitte, Milliseconds Make Millions',
    link: 'https://www.thinkwithgoogle.com/_qs/documents/9757/Milliseconds_Make_Millions_report_hQYAbZJ.pdf',
    accent: '#14b8a6', label: '01',
  },
  {
    id: 2, displayNumber: '60', numericFrom: 100, numericTo: 60, suffix: '%', prefix: 'über', isX: false, countDown: true,
    thesis: 'weniger Kaufwahrscheinlichkeit nach negativer mobiler Erfahrung.',
    copy: 'Wer mobil nicht überzeugt, verliert reale Kaufbereitschaft — dauerhaft.',
    source: 'Google / Think with Google',
    link: 'https://www.thinkwithgoogle.com/marketing-strategies/app-and-mobile/mobile-site-user-experience-micro-moments/',
    accent: '#10b981', label: '02',
  },
  {
    id: 3, displayNumber: '47', numericFrom: 0, numericTo: 47, suffix: '%', prefix: '', isX: false, countDown: false,
    thesis: 'der Nutzer erwarten, dass eine Webseite in 2 Sekunden oder weniger lädt.',
    copy: 'Je schneller eine Seite reagiert, desto höher ist die Chance auf Interaktion, Vertrauen und Conversion.',
    source: 'Cloudflare, How website performance affects conversion rates',
    link: 'https://www.cloudflare.com/learning/performance/more/website-performance-conversion-rates/',
    accent: '#14b8a6', label: '03',
  },
  {
    id: 4, displayNumber: '81', numericFrom: 0, numericTo: 81, suffix: '%', prefix: '', isX: false, countDown: false,
    thesis: 'der Käufer recherchieren online, bevor sie eine Kaufentscheidung treffen.',
    copy: 'Die Website ist oft der erste echte Berührungspunkt — und der Moment, in dem Vertrauen entsteht oder verloren geht.',
    source: 'Network Solutions, Top 50+ Small Business Website Statistics',
    link: 'https://www.networksolutions.com/blog/small-business-website-statistics/',
    accent: '#10b981', label: '04',
  },
  {
    id: 5, displayNumber: '400', numericFrom: 0, numericTo: 400, suffix: '%', prefix: '+', isX: false, countDown: false,
    thesis: 'mehr Conversion sind laut UX-Daten mit starkem User Experience Design möglich.',
    copy: 'Gute Gestaltung ist kein Selbstzweck. Sie hilft Menschen, schneller zu verstehen und häufiger zu handeln.',
    source: 'Hostinger, 28 essential web design statistics for 2026',
    link: 'https://www.hostinger.com/tutorials/web-design-statistics',
    accent: '#14b8a6', label: '05',
  },
  {
    id: 6, displayNumber: '11', numericFrom: 0, numericTo: 11, suffix: '%', prefix: '+', isX: false, countDown: false,
    thesis: 'höhere Conversion werden Websites mit responsive Design zugeschrieben.',
    copy: 'Wer mobil sauber funktioniert, macht vorhandenen Traffic profitabler.',
    source: 'Hostinger, 28 essential web design statistics for 2026',
    link: 'https://www.hostinger.com/tutorials/web-design-statistics',
    accent: '#10b981', label: '06',
  },
  {
    id: 7, displayNumber: '46', numericFrom: 0, numericTo: 46, suffix: '%', prefix: '+', isX: false, countDown: false,
    thesis: 'schnelleres Wachstum wird Unternehmen mit professioneller Website zugeschrieben.',
    copy: 'Eine gute Website ist kein nettes Extra. Sie kann Sichtbarkeit, Vertrauen und Nachfrage strukturell verstärken.',
    source: 'Debtech LLC, 2025 Small Biz Website Stats',
    link: 'https://www.debtechllc.com/blog/details/website-development-statistics-small-businesses-need-to-know-in-2025',
    accent: '#14b8a6', label: '07',
  },
  {
    id: 8, displayNumber: '3', numericFrom: 1, numericTo: 3, suffix: 'x', prefix: '', isX: true, countDown: false,
    thesis: 'so stark stiegen Leads nach responsive Relaunch und Mobile-Optimierung in 90 Tagen.',
    copy: 'Wenn Performance, mobile Nutzung und klare Nutzerführung zusammenkommen, kann aus einer Website wieder ein echter Vertriebskanal werden.',
    source: 'Debtech LLC, 2025 Biz Website Stats',
    link: 'https://www.debtechllc.com/blog/details/website-development-statistics-small-businesses-need-to-know-in-2025',
    accent: '#10b981', label: '08',
  },
];

function useInView(threshold = 0.25) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setInView(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

function AnimatedNumber({ from, to, isFloat }: { from: number; to: number; isFloat: boolean }) {
  const [val, setVal] = useState(from);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const dur = 1400;
        const start = performance.now();
        const tick = (now: number) => {
          const t = Math.min((now - start) / dur, 1);
          const e = 1 - Math.pow(1 - t, 3);
          const v = from + (to - from) * e;
          setVal(isFloat ? parseFloat(v.toFixed(1)) : Math.round(v));
          if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [from, to, isFloat]);
  return <span ref={ref}>{val}</span>;
}

function StatCard({ stat, index }: { stat: typeof stats[0]; index: number }) {
  const { theme } = useTheme();
  const isEven = index % 2 === 0;
  const isFloat = !Number.isInteger(stat.numericTo);

  return (
    <div className="relative w-full overflow-hidden" style={{ backgroundColor: '#0D0D0D' }}>
      <div className="absolute inset-0 pointer-events-none" style={{
        background: `radial-gradient(ellipse 60% 70% at ${isEven ? '15%' : '85%'} 50%, ${stat.accent}18 0%, transparent 65%)`,
      }} />
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: `linear-gradient(${stat.accent}08 1px, transparent 1px), linear-gradient(90deg, ${stat.accent}08 1px, transparent 1px)`,
        backgroundSize: '60px 60px',
      }} />
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${stat.accent}35, transparent)` }} />
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-8 md:px-16 py-10 sm:py-16 md:py-24 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-24 items-center">
        <NumberSide stat={stat} isEven={isEven} isFloat={isFloat} theme={theme} />
        <TextSide stat={stat} isEven={isEven} theme={theme} />
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${stat.accent}15, transparent)` }} />
    </div>
  );
}

function NumberSide({ stat, isEven, isFloat, theme }: { stat: typeof stats[0]; isEven: boolean; isFloat: boolean; theme: ReturnType<typeof useTheme>['theme'] }) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setInView(true); }, { threshold: 0.25 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  const bigFontSize = stat.numericTo >= 100 ? 'clamp(60px, 11vw, 160px)' : 'clamp(72px, 14vw, 200px)';
  return (
    <div
      ref={ref}
      className={`flex flex-col items-start ${!isEven ? 'md:order-2' : ''}`}
      style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(50px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}
    >
      <span className="text-xs font-mono tracking-[0.4em] uppercase mb-3" style={{ color: `${stat.accent}60` }}>{stat.label}</span>
      {stat.prefix && stat.prefix !== '+' && stat.prefix !== '-' && (
        <span className="text-base font-bold tracking-widest uppercase mb-1" style={{ fontFamily: theme.fonts.heading, color: stat.accent }}>{stat.prefix}</span>
      )}
      <div
        className="font-black leading-none select-none"
        style={{
          fontFamily: theme.fonts.heading,
          fontSize: bigFontSize,
          background: 'linear-gradient(135deg, #FFFFFF 0%, #10b981 60%, rgba(255,255,255,0.7) 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          lineHeight: 0.88,
        }}
      >
        {stat.isX ? (
          <><AnimatedNumber from={stat.numericFrom} to={stat.numericTo} isFloat={isFloat} /><span style={{ fontSize: '0.45em' }}>x</span></>
        ) : (
          <>
            {(stat.prefix === '+' || stat.prefix === '-') && stat.prefix}
            <AnimatedNumber from={stat.numericFrom} to={stat.numericTo} isFloat={isFloat} />
            <span style={{ fontSize: '0.42em' }}>{stat.suffix}</span>
          </>
        )}
      </div>
    </div>
  );
}

function TextSide({ stat, isEven, theme }: { stat: typeof stats[0]; isEven: boolean; theme: ReturnType<typeof useTheme>['theme'] }) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setInView(true); }, { threshold: 0.35 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`flex flex-col gap-4 ${!isEven ? 'md:order-1' : ''}`}
      style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(30px)', transition: 'opacity 0.7s ease 0.15s, transform 0.7s ease 0.15s' }}
    >
      <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-[2rem] font-bold leading-tight" style={{ fontFamily: theme.fonts.heading, color: '#FFFFFF' }}>
        {stat.thesis}
      </h3>
      <div className="h-px" style={{ width: inView ? '100px' : '0px', background: `linear-gradient(90deg, ${stat.accent}, transparent)`, transition: 'width 1.2s ease 0.3s' }} />
      <p className="text-sm sm:text-base leading-relaxed" style={{ color: 'rgba(255,255,255,0.4)' }}>{stat.copy}</p>
      <a
        href={stat.link}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 text-xs tracking-widest uppercase hover:opacity-80 transition-opacity font-mono"
        style={{ color: `${stat.accent}60` }}
      >
        <span className="inline-block w-4 h-px flex-shrink-0" style={{ backgroundColor: `${stat.accent}60` }} />
        <span className="truncate">{stat.source}</span>
        <i className="ri-external-link-line text-xs flex-shrink-0"></i>
      </a>
    </div>
  );
}

function StatisticsSection() {
  const { theme } = useTheme();
  const { ref, inView } = useInView(0.2);
  return (
    <section id="statistiken" style={{ backgroundColor: '#0D0D0D', fontFamily: theme.fonts.body }}>
      <div ref={ref} className="py-10 sm:py-14 md:py-20 px-4 sm:px-6 text-left sm:text-center" style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(30px)', transition: 'opacity 0.8s ease, transform 0.8s ease' }}>
        <span className="text-xs tracking-[0.4em] uppercase font-mono block mb-3" style={{ color: `${theme.colors.accent}70` }}>Fakten &amp; Zahlen</span>
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-black" style={{ fontFamily: theme.fonts.heading, color: '#FFFFFF' }}>Was die Daten sagen</h2>
      </div>
      {stats.map((stat, i) => <StatCard key={stat.id} stat={stat} index={i} />)}
      <div className="py-12 md:py-20 flex justify-center px-4 sm:px-6" style={{ backgroundColor: '#0D0D0D' }}>
        <Link to="/termin-buchen">
          <button className="group relative px-10 py-4 font-bold text-sm tracking-widest uppercase rounded-full overflow-hidden whitespace-nowrap cursor-pointer transition-all duration-300" style={{ border: `1.5px solid ${theme.colors.accent}50`, color: '#FFFFFF', background: 'transparent' }}>
            <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-full" style={{ background: `linear-gradient(135deg, ${theme.colors.secondary}CC, ${theme.colors.accent}CC)` }} />
            <span className="relative z-10">Relaunch anfragen</span>
          </button>
        </Link>
      </div>
    </section>
  );
}

function Comparison() {
  const { theme } = useTheme();
  const { ref, inView } = useInView(0.2);
  const oldSite = [
    { icon: 'ri-time-line', text: 'Langsame Ladezeiten' },
    { icon: 'ri-question-line', text: 'Unklare Navigation' },
    { icon: 'ri-smartphone-line', text: 'Mobil schwach' },
    { icon: 'ri-shield-cross-line', text: 'Wenig Vertrauen' },
    { icon: 'ri-arrow-down-line', text: 'Besucher verlieren' },
  ];
  const modernSite = [
    { icon: 'ri-flashlight-line', text: 'Schnell & performant' },
    { icon: 'ri-compass-3-line', text: 'Klar geführt' },
    { icon: 'ri-smartphone-line', text: 'Mobile-first' },
    { icon: 'ri-shield-check-line', text: 'Professioneller Eindruck' },
    { icon: 'ri-arrow-up-line', text: 'Anfragen gewinnen' },
  ];
  return (
    <section ref={ref} className="py-12 sm:py-20 md:py-32 px-4 sm:px-6" style={{ backgroundColor: '#FAFAFA', fontFamily: theme.fonts.body }}>
      <div className="max-w-5xl mx-auto">
        <div className="text-left mb-10 sm:mb-14 md:mb-20" style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 0.8s ease, transform 0.8s ease' }}>
          <span className="text-xs tracking-[0.35em] uppercase font-semibold" style={{ color: theme.colors.accent }}>Der Vergleich</span>
          <h2 className="mt-3 text-2xl sm:text-3xl md:text-5xl font-black leading-tight" style={{ fontFamily: theme.fonts.heading, color: theme.colors.text }}>Der Unterschied ist messbar</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 md:gap-8">
          <div className="p-5 sm:p-8 md:p-10 rounded-2xl" style={{ backgroundColor: '#FFFFFF', border: '2px solid #EBEBEB', opacity: inView ? 1 : 0, transform: inView ? 'translateX(0)' : 'translateX(-40px)', transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s' }}>
            <div className="flex items-center gap-3 mb-6 sm:mb-8">
              <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FEE2E2' }}>
                <i className="ri-close-line text-xl" style={{ color: '#DC2626' }}></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold" style={{ fontFamily: theme.fonts.heading, color: theme.colors.text }}>Veraltete Website</h3>
            </div>
            <div className="space-y-3 sm:space-y-4">
              {oldSite.map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FEE2E2' }}>
                    <i className={item.icon} style={{ color: '#DC2626' }}></i>
                  </div>
                  <p className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>{item.text}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="p-5 sm:p-8 md:p-10 rounded-2xl" style={{ backgroundColor: '#FFFFFF', border: `2px solid ${theme.colors.accent}`, opacity: inView ? 1 : 0, transform: inView ? 'translateX(0)' : 'translateX(40px)', transition: 'opacity 0.8s ease 0.35s, transform 0.8s ease 0.35s' }}>
            <div className="flex items-center gap-3 mb-6 sm:mb-8">
              <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `linear-gradient(135deg, ${theme.colors.secondary}, ${theme.colors.accent})` }}>
                <i className="ri-check-line text-xl text-white"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold" style={{ fontFamily: theme.fonts.heading, color: theme.colors.text }}>Moderne Website</h3>
            </div>
            <div className="space-y-3 sm:space-y-4">
              {modernSite.map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `linear-gradient(135deg, ${theme.colors.secondary}, ${theme.colors.accent})` }}>
                    <i className={`${item.icon} text-white`}></i>
                  </div>
                  <p className="text-sm font-medium" style={{ color: theme.colors.text }}>{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const { theme } = useTheme();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const { ref, inView } = useInView(0.15);
  const faqs = [
    { q: 'Lohnt sich ein Relaunch?', a: 'Wenn Ihre Website veraltet ist, langsam lädt oder mobil nicht funktioniert, verlieren Sie messbar Anfragen. Ein Relaunch kann Performance, Vertrauen und Conversion deutlich verbessern.' },
    { q: 'Bringt eine moderne Website mehr Anfragen?', a: 'Ja. Studien zeigen: Schnellere Ladezeiten, bessere mobile Nutzung und klare Nutzerführung führen zu mehr Interaktionen, mehr Vertrauen und mehr Conversions.' },
    { q: 'Wie wichtig ist Ladezeit?', a: '47 % der Nutzer erwarten, dass eine Seite in 2 Sekunden lädt. Schon 0,1 Sekunden Verbesserung können die Conversion um bis zu 10 % steigern.' },
    { q: 'Warum ist mobile Optimierung entscheidend?', a: 'Über 60 % der Nutzer kaufen nach einer schlechten mobilen Erfahrung nicht. Wer mobil nicht überzeugt, verliert reale Kaufbereitschaft.' },
  ];
  return (
    <section ref={ref} className="py-12 sm:py-20 md:py-32 px-4 sm:px-6" style={{ backgroundColor: '#FFFFFF', fontFamily: theme.fonts.body }}>
      <div className="max-w-3xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-black mb-12 md:mb-16 text-left sm:text-center" style={{ fontFamily: theme.fonts.heading, color: theme.colors.text, opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(30px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}>
          Häufige Fragen
        </h2>
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div key={i} className="rounded-xl overflow-hidden transition-all duration-300" style={{ backgroundColor: openIndex === i ? `${theme.colors.accent}0D` : '#FAFAFA', border: `2px solid ${openIndex === i ? theme.colors.accent : 'transparent'}`, opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(20px)', transition: `opacity 0.6s ease ${0.1 + i * 0.08}s, transform 0.6s ease ${0.1 + i * 0.08}s, background-color 0.3s, border-color 0.3s` }}>
              <button onClick={() => setOpenIndex(openIndex === i ? null : i)} className="w-full px-6 md:px-8 py-5 flex items-center justify-between text-left cursor-pointer gap-3">
                <span className="text-base font-bold" style={{ color: theme.colors.text }}>{faq.q}</span>
                <i className={`ri-arrow-${openIndex === i ? 'up' : 'down'}-s-line text-xl flex-shrink-0 transition-transform`} style={{ color: theme.colors.accent }}></i>
              </button>
              {openIndex === i && (
                <div className="px-6 md:px-8 pb-5">
                  <p className="text-sm leading-relaxed" style={{ color: theme.colors.textSecondary }}>{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  const { theme } = useTheme();
  const { ref, inView } = useInView(0.3);
  return (
    <section ref={ref} className="py-12 sm:py-20 md:py-32 px-4 sm:px-6 relative overflow-hidden" style={{ backgroundColor: '#0D0D0D', fontFamily: theme.fonts.body }}>
      <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(ellipse 70% 60% at 50% 50%, ${theme.colors.secondary}18 0%, transparent 65%)` }} />
      <div className="max-w-4xl mx-auto text-left relative z-10" style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 0.9s ease, transform 0.9s ease' }}>
        <span className="text-xs tracking-[0.4em] uppercase font-mono block mb-4" style={{ color: `${theme.colors.accent}70` }}>Jetzt handeln</span>
        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black mb-5 sm:mb-6 leading-tight text-white" style={{ fontFamily: theme.fonts.heading }}>
          Machen Sie aus Ihrer Website<br />
          <span style={{ background: `linear-gradient(135deg, ${theme.colors.secondary} 0%, ${theme.colors.accent} 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            einen echten Vertriebskanal
          </span>
        </h2>
        <p className="text-sm sm:text-base md:text-lg mb-8 sm:mb-10 leading-relaxed" style={{ color: 'rgba(255,255,255,0.45)' }}>
          Wenn Ihre Website veraltet, langsam oder unklar ist, verschenken Sie messbares Potenzial. Wir helfen Ihnen, das zu ändern.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-start sm:justify-center">
          <Link to="/termin-buchen">
            <button className="w-full sm:w-auto px-8 sm:px-10 py-4 font-bold text-base rounded-xl transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer" style={{ background: `linear-gradient(135deg, ${theme.colors.secondary} 0%, ${theme.colors.accent} 100%)`, color: '#FFFFFF', boxShadow: `0 12px 30px ${theme.colors.accent}40` }}>
              Relaunch anfragen
            </button>
          </Link>
          <Link to="/ueber-uns">
            <button className="w-full sm:w-auto px-8 sm:px-10 py-4 font-semibold text-base rounded-xl transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer" style={{ backgroundColor: 'rgba(255,255,255,0.07)', color: '#FFFFFF', border: '1.5px solid rgba(255,255,255,0.18)' }}>
              Mehr über Seitenschmied
            </button>
          </Link>
        </div>
      </div>
    </section>
  );
}

function Sources() {
  const { theme } = useTheme();
  return (
    <section className="py-10 sm:py-14 md:py-20 px-4 sm:px-6" style={{ backgroundColor: '#FAFAFA', fontFamily: theme.fonts.body }}>
      <div className="max-w-4xl mx-auto">
        <h3 className="text-xl font-bold mb-8" style={{ fontFamily: theme.fonts.heading, color: theme.colors.text }}>Quellen</h3>
        <div className="space-y-4">
          {stats.map((stat, i) => (
            <div key={i} className="flex items-start gap-4">
              <span className="text-xs font-mono font-bold mt-1 flex-shrink-0" style={{ color: theme.colors.accent }}>[{i + 1}]</span>
              <div className="min-w-0">
                <p className="text-sm font-medium mb-1" style={{ color: theme.colors.text }}>{stat.source}</p>
                <a href={stat.link} target="_blank" rel="noopener noreferrer" className="text-xs hover:opacity-70 transition-opacity break-all" style={{ color: theme.colors.textSecondary }}>{stat.link}</a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
function WebsiteImpactContent() {
  const { theme, setCurrentLook } = useTheme();
  const navigate = useNavigate();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://seitenschmied.com';

  const handleRefsClick = () => {
    setCurrentLook('seitenschmied');
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('transformation');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        setTimeout(() => window.dispatchEvent(new CustomEvent('openReferenzen')), 600);
      }
    }, 150);
  };

  useSEO({
    title: 'Was eine moderne Website wirklich bringt – Fakten & Zahlen | Seitenschmied',
    description: 'Mehr Vertrauen, mehr Anfragen, mehr Wirkung: Erfahren Sie anhand echter Daten und Studien, wie eine moderne Website messbar mehr Conversion, Leads und Umsatz bringen kann.',
    keywords: 'website relaunch, moderne website, website conversion, ladezeit optimierung, mobile optimierung',
    canonical: `${siteUrl}/website-wirkung`,
    ogImage: 'https://static.readdy.ai/image/5464a8e208be2c466098e318f0dedf87/06f7ee287cfacc7a314e7ca2f415ab12.png',
  });

  return (
    <div className="min-h-screen" style={{ fontFamily: theme.fonts.body }}>

      <SiteNav
        activePage="website-impact"
        navId="navbar-impact"
        onRefsClick={handleRefsClick}
      />

      <PageHero
        label="Fakten & Zahlen"
        headline={
          <>
            Was eine moderne Website{' '}
            <span style={gradientTextStyle}>wirklich bringt</span>
          </>
        }
        subtext="Keine Meinungen, keine Versprechen — nur Daten aus echten Studien, die zeigen, wie viel Potenzial Ihre Website gerade ungenutzt lässt."
        backgroundImage="https://static.readdy.ai/image/5464a8e208be2c466098e318f0dedf87/7ecce3f6d261e69ebe63dea2d096613e.jpeg"
        backgroundPosition="right center"
        ctas={[
          {
            label: 'Fakten ansehen',
            variant: 'primary',
            onClick: () => document.getElementById('statistiken')?.scrollIntoView({ behavior: 'smooth' }),
          },
          { label: 'Projekt anfragen', to: '/termin-buchen', variant: 'secondary' },
        ]}
      />

      <StatisticsSection />
      <Comparison />
      <CTA />
      <FAQSection />
      <Sources />
      <SiteFooter />
    </div>
  );
}

export default function WebsiteImpact() {
  return (
    <ThemeProvider>
      <WebsiteImpactContent />
    </ThemeProvider>
  );
}
